import React, { useState, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  Button,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from "@mui/material"
import PeopleIcon from "@mui/icons-material/People"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import SearchIcon from "@mui/icons-material/Search"
import StorefrontIcon from "@mui/icons-material/Storefront"
import {
  getAppUsers,
  createAppUser,
  updateAppUser,
  deleteAppUser,
  getAppRoles,
  getCustomers,
} from "../../helpers/api_helper"
import { isB2BUser } from "../../constants/permissions"

const UsersTab = ({ onSnackbar }) => {
  const { t } = useTranslation()

  // State
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    email: "",
    name: "",
    first_name: "",
    last_name: "",
    phone: "",
    password: "",
    role_id: "",
    customer_id: null,
    is_active: true,
  })

  // Fetch data on mount
  useEffect(() => {
    fetchUsers()
    fetchRoles()
    fetchCustomers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await getAppUsers()
      setUsers(res || [])
    } catch (err) {
      console.error("Error fetching users:", err)
      onSnackbar?.({ open: true, message: t("Error fetching users"), severity: "error" })
    }
    setLoading(false)
  }

  const fetchRoles = async () => {
    setRolesLoading(true)
    try {
      const res = await getAppRoles()
      setRoles(res || [])
    } catch (err) {
      console.error("Error fetching roles:", err)
    }
    setRolesLoading(false)
  }

  const fetchCustomers = async () => {
    try {
      const res = await getCustomers()
      setCustomers(Array.isArray(res) ? res : res?.resource || [])
    } catch (err) {
      console.error("Error fetching customers:", err)
    }
  }

  // Check if the selected role is B2B
  const selectedRole = useMemo(() => {
    if (!form.role_id) return null
    return roles.find(r => r.id === form.role_id)
  }, [form.role_id, roles])

  const isSelectedRoleB2B = useMemo(() => {
    if (!selectedRole) return false
    return isB2BUser(selectedRole.permissions)
  }, [selectedRole])

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user)
      setForm({
        email: user.email || "",
        name: user.name || "",
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        password: "",
        role_id: user.role_id || "",
        customer_id: user.customer_id || null,
        is_active: user.is_active !== false,
      })
    } else {
      setEditingUser(null)
      setForm({
        email: "",
        name: "",
        first_name: "",
        last_name: "",
        phone: "",
        password: "",
        role_id: "",
        customer_id: null,
        is_active: true,
      })
    }
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingUser(null)
    setForm({
      email: "",
      name: "",
      first_name: "",
      last_name: "",
      phone: "",
      password: "",
      role_id: "",
      customer_id: null,
      is_active: true,
    })
  }

  const handleSave = async () => {
    if (!form.email.trim()) {
      onSnackbar?.({ open: true, message: t("Email is required"), severity: "error" })
      return
    }
    if (!editingUser && !form.password.trim()) {
      onSnackbar?.({ open: true, message: t("Password is required for new user"), severity: "error" })
      return
    }

    setSaving(true)
    try {
      const userData = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || `${form.first_name.trim()} ${form.last_name.trim()}`.trim() || form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        role_id: form.role_id || null,
        customer_id: isSelectedRoleB2B ? (form.customer_id || null) : null,
        is_active: form.is_active,
      }

      if (form.password.trim()) {
        userData.password = form.password.trim()
      }

      if (editingUser) {
        await updateAppUser(editingUser.id, userData)
        onSnackbar?.({ open: true, message: t("User updated successfully"), severity: "success" })
      } else {
        await createAppUser(userData)
        onSnackbar?.({ open: true, message: t("User created successfully"), severity: "success" })
      }

      handleCloseDialog()
      fetchUsers()
    } catch (err) {
      console.error("Error saving user:", err)
      onSnackbar?.({ open: true, message: err.message || t("Error saving user"), severity: "error" })
    }
    setSaving(false)
  }

  const handleDelete = async (user) => {
    if (!window.confirm(t("Are you sure you want to delete this user?"))) return

    try {
      await deleteAppUser(user.id)
      onSnackbar?.({ open: true, message: t("User deleted successfully"), severity: "success" })
      fetchUsers()
    } catch (err) {
      console.error("Error deleting user:", err)
      onSnackbar?.({ open: true, message: err.message || t("Error deleting user"), severity: "error" })
    }
  }

  // Filter users by search term
  const filteredUsers = users.filter(user => {
    const search = searchTerm.toLowerCase()
    return (
      user.email?.toLowerCase().includes(search) ||
      user.name?.toLowerCase().includes(search) ||
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search)
    )
  })

  // Get role name by ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId)
    return role?.name || "-"
  }

  // Get customer name by ID
  const getCustomerName = (customerId) => {
    if (!customerId) return null
    const customer = customers.find(c => c.id === customerId)
    return customer?.name || customer?.companyName || `#${customerId}`
  }

  // Get role type chip
  const getRoleTypeChip = (roleId) => {
    const role = roles.find(r => r.id === roleId)
    if (!role?.permissions) return null
    if (isB2BUser(role.permissions)) return <Chip label="B2B" size="small" sx={{ ml: 0.5, height: 18, fontSize: "0.6rem", fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }} />
    return null
  }

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#dbeafe", color: "#3b82f6" }}>
            <PeopleIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("App Users")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Manage users for this application")}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          {t("Add User")}
        </Button>
      </Stack>

      {/* Search */}
      <TextField
        size="small"
        placeholder={t("Search users...")}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 20, color: "#9ca3af" }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          width: 300,
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
        }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Email")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Name")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Role")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Customer")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Status")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#6b7280" }}>
                    {searchTerm ? t("No users match your search") : t("No users found")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} hover sx={{ "&:hover": { bgcolor: "#f8fafc" } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "-"}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Chip
                          label={getRoleName(user.role_id)}
                          size="small"
                          sx={{
                            bgcolor: "#e0e7ff",
                            color: "#4338ca",
                            fontWeight: 500,
                          }}
                        />
                        {getRoleTypeChip(user.role_id)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {user.customer_id ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <StorefrontIcon sx={{ fontSize: 14, color: "#6b7280" }} />
                          <Typography variant="body2" fontSize="0.8rem" color="#374151">
                            {getCustomerName(user.customer_id)}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="#d1d5db">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.is_active !== false ? t("Active") : t("Inactive")}
                        size="small"
                        sx={{
                          bgcolor: user.is_active !== false ? "#dcfce7" : "#fee2e2",
                          color: user.is_active !== false ? "#16a34a" : "#dc2626",
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleOpenDialog(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(user)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 2 }}>
          {editingUser ? t("Edit User") : t("Add User")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label={t("Email")}
              type="email"
              value={form.email}
              onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                size="small"
                label={t("First Name")}
                value={form.first_name}
                onChange={(e) => setForm(prev => ({ ...prev, first_name: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                size="small"
                label={t("Last Name")}
                value={form.last_name}
                onChange={(e) => setForm(prev => ({ ...prev, last_name: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
            <TextField
              fullWidth
              size="small"
              label={t("Phone")}
              value={form.phone}
              onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t("Role")}</InputLabel>
              <Select
                value={form.role_id}
                label={t("Role")}
                onChange={(e) => setForm(prev => ({ ...prev, role_id: e.target.value, customer_id: null }))}
                sx={{ borderRadius: 2 }}
                disabled={rolesLoading}
              >
                <MenuItem value="">{t("No Role")}</MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span>{role.name}</span>
                      {isB2BUser(role.permissions) && (
                        <Chip label="B2B" size="small" sx={{ height: 18, fontSize: "0.6rem", bgcolor: "#dbeafe", color: "#1e40af" }} />
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Customer binding for B2B roles */}
            {isSelectedRoleB2B && (
              <Autocomplete
                size="small"
                options={customers}
                getOptionLabel={(option) => option.name || option.companyName || `#${option.id}`}
                value={customers.find(c => c.id === form.customer_id) || null}
                onChange={(_, newValue) => {
                  setForm(prev => ({ ...prev, customer_id: newValue?.id || null }))
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Linked Customer")}
                    placeholder={t("Select customer for B2B user...")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <StorefrontIcon sx={{ fontSize: 18, color: "#6b7280" }} />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography fontSize="0.85rem" fontWeight={500}>{option.name || option.companyName}</Typography>
                      {option.email && <Typography fontSize="0.7rem" color="#6b7280">{option.email}</Typography>}
                    </Stack>
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText={t("No customers found")}
              />
            )}

            <TextField
              fullWidth
              size="small"
              label={editingUser ? t("New Password (leave empty to keep current)") : t("Password")}
              type="password"
              value={form.password}
              onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
              required={!editingUser}
              helperText={editingUser ? t("Only fill if you want to change the password") : ""}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t("Status")}</InputLabel>
              <Select
                value={form.is_active ? "active" : "inactive"}
                label={t("Status")}
                onChange={(e) => setForm(prev => ({ ...prev, is_active: e.target.value === "active" }))}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="active">{t("Active")}</MenuItem>
                <MenuItem value="inactive">{t("Inactive")}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UsersTab
